import store from "@/store";
import {
  Annotation,
  AnnotationCard,
  AnnotationCardType,
  AnnotationPosition,
  AnnotationType,
} from "@/store/annotation/annotationModel";
import {
  CREATE_ANNOTATION,
  UPDATE_ANNOTATION,
} from "@/store/annotation/annotationAction";
import { Slide, SlideAnnotation } from "@/store/slides2Video/slides2VideoModels";
import { VideoMetadata } from "@/components/videoImageBackground/VideoChunckGenerator.vue";
import { clone } from "@/utils/cloneUtil";
import { parseTimestamp } from "@/utils/timeLineUtils";

const FRAME_RATIO = 16 / 9;

/**
 * Convert annotation coordinates from video space to 16/9 frame space.
 * The LLM gives percentages relative to the video, but annotations
 * are rendered in a 16/9 frame that may have pillarbox/letterbox around the video.
 */
export function convertVideoToFrameCoordinates(
  posX: number,
  posY: number,
  width: number,
  height: number,
  videoMetadata: VideoMetadata | null
): { posX: number; posY: number; width: number; height: number } {
  if (!videoMetadata) {
    return { posX, posY, width, height };
  }

  const videoRatio = videoMetadata.videoWidth / videoMetadata.videoHeight;

  let scaleX = 1;
  let scaleY = 1;
  let offsetX = 0;
  let offsetY = 0;

  if (videoRatio < FRAME_RATIO) {
    // Video is narrower than 16/9 (e.g. portrait/9:16) → pillarbox (black bars on sides)
    const videoWidthInFrame = videoRatio / FRAME_RATIO;
    scaleX = videoWidthInFrame;
    offsetX = ((1 - videoWidthInFrame) / 2) * 100;
  } else if (videoRatio > FRAME_RATIO) {
    // Video is wider than 16/9 → letterbox (black bars top/bottom)
    const videoHeightInFrame = FRAME_RATIO / videoRatio;
    scaleY = videoHeightInFrame;
    offsetY = ((1 - videoHeightInFrame) / 2) * 100;
  }

  return {
    posX: offsetX + posX * scaleX,
    posY: offsetY + posY * scaleY,
    width: width * scaleX,
    height: height * scaleY,
  };
}

/**
 * Create annotations on the weet based on slide.annotations.
 * Handles coordinate conversion from video space to 16/9 frame space.
 */
export async function createAutoAnnotations(
  slide: Slide,
  sectionStartTime: number,
  videoMetadata: VideoMetadata | null
): Promise<string[]> {
  const createdIDs: string[] = [];
  if (!slide.annotations || slide.annotations.length === 0) {
    return createdIDs;
  }
  const weetID = store.getters.getEditingWeetID;
  if (!weetID) {
    return createdIDs;
  }


  for (const slideAnnotation of slide.annotations) {

    let annotation: Annotation | null = null;

    if (slideAnnotation.type === "TEXT") {
      annotation = new Annotation();
      annotation.type = AnnotationType.TEXT;
      annotation.textContent = slideAnnotation.text;
      annotation.positionType = AnnotationPosition.FREE;
      annotation.colorFront = store.getters.getAnnotationColor || "#FFFFFF";
      annotation.colorBack =
        store.getters.getAnnotationBackgroundColor ||
        store.getters.getThemeColor;
    } else if (slideAnnotation.type === "BORDER_BOX") {
      console.log("Border box", slideAnnotation);
      annotation = new Annotation();
      annotation.type = AnnotationType.ZONE;
      annotation.positionType = AnnotationPosition.FREE;
      annotation.colorFront =
        store.getters.getAnnotationBackgroundColor ||
        store.getters.getThemeColor;
      annotation.colorBack = "transparent";
    } else if (slideAnnotation.type === "FOCUS_POINT") {
      console.log("focus point", slideAnnotation);
      annotation = new Annotation();
      annotation.type = AnnotationType.INTERACTION;
      annotation.positionType = AnnotationPosition.FREE;
      annotation.colorFront =
        store.getters.getAnnotationBackgroundColor ||
        store.getters.getThemeColor;
      annotation.width = 1;
      annotation.height = 1;
      const focusConverted = convertVideoToFrameCoordinates(
        slideAnnotation.centerX,
        slideAnnotation.centerY,
        0,
        0,
        videoMetadata
      );
      annotation.positionX = focusConverted.posX - annotation.width / 2;
      annotation.positionY = focusConverted.posY - annotation.height / 2;
      annotation.annotationCard = new AnnotationCard();
      annotation.annotationCard.title = slideAnnotation.title || "";
      annotation.annotationCard.description =
        slideAnnotation.description || "";
    } else {
      console.log("ignored annotation type", slideAnnotation.type);
      continue;
    }

    // Convert timestamps relative to the section start to absolute timeline position
    const startSeconds = parseTimestamp(slideAnnotation.timestampStart);
    const endSeconds = parseTimestamp(slideAnnotation.timestampEnd);
    annotation.time =
      sectionStartTime + (startSeconds - slide.videoTimestampStart) * 1000;
    annotation.duration = (endSeconds - startSeconds) * 1000;

    // Compute position and size from boundingBox (values are percentages relative to video)
    if (
      slideAnnotation.boundingBox &&
      slideAnnotation.type !== "FOCUS_POINT"
    ) {
      const bb = slideAnnotation.boundingBox;
      let width = bb.right - bb.left;
      let height = bb.bottom - bb.top;
      let posX = bb.left;
      let posY = bb.top;

      // Add 5% padding on each side for BORDER_BOX
      if (slideAnnotation.type === "BORDER_BOX") {
        posX = posX - 5;
        posY = posY - 5;
        width = width + 10;
        height = height + 10;
      }

      const converted = convertVideoToFrameCoordinates(
        posX,
        posY,
        width,
        height,
        videoMetadata
      );

      annotation.positionX = Math.max(0, Math.min(100, converted.posX));
      annotation.positionY = Math.max(0, Math.min(100, converted.posY));
      annotation.width = Math.max(0, Math.min(100, converted.width));
      annotation.height = Math.max(0, Math.min(100, converted.height));
    }

    const createdAnnotation = await store.dispatch(CREATE_ANNOTATION, {
      weetID,
      annotation,
    });

    if (createdAnnotation && createdAnnotation.annotationID) {
      createdIDs.push(createdAnnotation.annotationID);
    }

    // For FOCUS_POINT, save the annotationCard via UPDATE_ANNOTATION
    if (slideAnnotation.type === "FOCUS_POINT" && createdAnnotation) {
      const clonedAnnotation = clone(createdAnnotation);
      clonedAnnotation.annotationCard.title = slideAnnotation.title || "";
      clonedAnnotation.annotationCard.description =
        slideAnnotation.description || "";
      clonedAnnotation.annotationCard.type = AnnotationCardType.SIMPLE_CARD;
      await store.dispatch(UPDATE_ANNOTATION, {
        weetID,
        annotation: clonedAnnotation,
      });
    }
  }
  return createdIDs;
}
